import useGetProjectByIdOverview from '@/api/queries/project/useGetProjectByIdOverview';
import { useRegion } from '@/api/rest/users/useRegion';
import { getInvoiceCapExceededMessage } from '@payaca/helpers/grantHelper';
import { getInvoiceLinesValue } from '@payaca/helpers/invoiceHelper';
import { useDeal, useInvoicesForDeal } from '@payaca/store/hooks/appState';
import { InvoiceLine } from '@payaca/types/invoiceTypes';
import { useCallback } from 'react';

type EditedInvoiceLine = Pick<InvoiceLine, 'value'>;

export type InvoiceLineBounds = {
  getHeadroom: (invoiceLines: EditedInvoiceLine[]) => number;
  getInvoiceValueError: (invoiceLines: EditedInvoiceLine[]) => string | null;
};

export const useInvoiceLineBounds = ({
  dealId,
  invoiceId,
}: {
  dealId?: number;
  invoiceId: number;
}): InvoiceLineBounds => {
  const region = useRegion();
  const deal = useDeal(dealId);
  const { project } = useGetProjectByIdOverview(deal?.publicId);
  const invoices = useInvoicesForDeal(dealId);

  const acceptedValue = deal?.acceptedValue ?? 0;
  const totalPayable = project?.totals.totalPayable.value ?? acceptedValue;

  // Taken from the other invoices rather than the project's invoiced totals,
  // which include this invoice as last saved and lag behind each save.
  const otherCustomerInvoicedValue = invoices
    .filter((x) => x.id !== invoiceId && !x.voidedAt && !x.isGrantInvoice)
    .reduce((sum, x) => sum + x.totalValue, 0);
  const maxInvoiceValue = Math.max(
    0,
    totalPayable - otherCustomerInvoicedValue
  );

  const getHeadroom = useCallback(
    (invoiceLines: EditedInvoiceLine[]) =>
      maxInvoiceValue - getInvoiceLinesValue(invoiceLines),
    [maxInvoiceValue]
  );

  const getInvoiceValueError = useCallback(
    (invoiceLines: EditedInvoiceLine[]) => {
      if (getHeadroom(invoiceLines) >= 0) return null;
      return getInvoiceCapExceededMessage({
        maxValue: maxInvoiceValue,
        totalPayable,
        acceptedValue,
        region,
      });
    },
    [getHeadroom, maxInvoiceValue, totalPayable, acceptedValue, region]
  );

  return { getHeadroom, getInvoiceValueError };
};
